import { useTranslation } from 'react-i18next'
import { formatClock } from '../utils'
import { ReportItemButton } from './ReportItemButton'
import type { ReportHandler } from '../types'

/**
 * Détail d'un item pour l'écran de fin, déjà mis en forme (prompt, bonne
 * réponse, explication en texte simple). La mise en forme dépend du type de
 * l'item — huit formes différentes, SPEC §4 — c'est à l'appelant de la
 * produire ; `ResultScreen` reste un composant d'affichage pur.
 */
export interface ResultScreenItemDetail {
  item_id: string
  prompt: string
  correct_answer: string
  is_correct: boolean
  explanation: string
}

export interface ResultScreenProps {
  readonly score: number
  readonly maxScore: number
  /** Pourcentage 0..100. */
  readonly accuracy: number
  readonly durationMs: number
  /** XP crédité pour la partie (SPEC §12 `finish` → `xp_earned`, §13.2). Absent : le bloc n'est pas rendu. */
  readonly xpEarned?: number
  readonly items: ResultScreenItemDetail[]
  readonly onReplay?: () => void
  readonly onExit?: () => void
  /** Absent : les boutons de signalement ne sont pas rendus. */
  readonly onReportItem?: ReportHandler
}

/**
 * Écran de fin de partie : score, précision, temps, puis le détail de chaque
 * item avec sa bonne réponse et son explication (SPEC §11.2 : un bouton de
 * signalement par item, affiché pendant cette même correction).
 */
export function ResultScreen({
  score,
  maxScore,
  accuracy,
  durationMs,
  xpEarned,
  items,
  onReplay,
  onExit,
  onReportItem,
}: ResultScreenProps) {
  const { t } = useTranslation()

  return (
    <section
      aria-label={t('practice.result.title')}
      className="flex flex-col gap-4 px-4 py-6 md:px-6 md:py-8"
    >
      <header className="flex flex-col items-center gap-1 text-center">
        <p className="font-display text-2xl font-bold text-on-surface">{t('practice.result.title')}</p>
        <p className="text-sm text-on-surface-variant">{t('practice.result.subtitle')}</p>
      </header>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3">
        <div className="flex flex-col items-center gap-0.5 rounded-2xl bg-surface-low p-3 text-center">
          <span className="font-display text-xl font-bold tabular-nums text-primary">
            {t('practice.result.score_value', { score, maxScore })}
          </span>
          <span className="text-[11px] text-on-surface-variant">{t('practice.result.score_label')}</span>
        </div>
        <div className="flex flex-col items-center gap-0.5 rounded-2xl bg-surface-low p-3 text-center">
          <span className="font-display text-xl font-bold tabular-nums text-secondary">
            {t('practice.result.accuracy_value', { accuracy })}
          </span>
          <span className="text-[11px] text-on-surface-variant">{t('practice.result.accuracy_label')}</span>
        </div>
        <div className="flex flex-col items-center gap-0.5 rounded-2xl bg-surface-low p-3 text-center">
          <span className="font-display text-xl font-bold tabular-nums text-on-surface">
            {formatClock(durationMs)}
          </span>
          <span className="text-[11px] text-on-surface-variant">{t('practice.result.duration_label')}</span>
        </div>
        {xpEarned !== undefined && (
          <div className="flex flex-col items-center gap-0.5 rounded-2xl bg-surface-low p-3 text-center">
            <span className="font-display text-xl font-bold tabular-nums text-primary">
              {t('practice.result.xp_value', { xp: xpEarned })}
            </span>
            <span className="text-[11px] text-on-surface-variant">{t('practice.result.xp_label')}</span>
          </div>
        )}
      </div>

      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <li
            key={item.item_id}
            className={[
              'flex flex-col gap-1.5 rounded-2xl border p-3',
              item.is_correct ? 'border-success/30 bg-success/5' : 'border-error/30 bg-error/5',
            ].join(' ')}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-on-surface">{item.prompt}</p>
              <span
                aria-label={item.is_correct ? t('practice.result.item_correct') : t('practice.result.item_incorrect')}
                className={item.is_correct ? 'shrink-0 text-success' : 'shrink-0 text-error'}
              >
                {item.is_correct ? '✓' : '✗'}
              </span>
            </div>

            <p className="text-xs text-on-surface-variant">
              <span className="font-semibold">{t('practice.result.correct_answer_label')} </span>
              {item.correct_answer}
            </p>

            {item.explanation && <p className="text-xs text-on-surface-variant">{item.explanation}</p>}

            {onReportItem && <ReportItemButton itemId={item.item_id} onReport={onReportItem} />}
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-2 md:flex-row">
        {onReplay && (
          <button
            type="button"
            onClick={onReplay}
            className="min-h-[44px] flex-1 rounded-full bg-primary px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            {t('practice.result.replay')}
          </button>
        )}
        {onExit && (
          <button
            type="button"
            onClick={onExit}
            className="min-h-[44px] flex-1 rounded-full bg-surface-low px-4 text-sm font-medium text-on-surface transition-colors hover:bg-surface-container"
          >
            {t('practice.result.exit')}
          </button>
        )}
      </div>
    </section>
  )
}
